import { uiConfig } from '@brand/config/ui.config';
import { Component, computed, inject, model, signal } from '@angular/core';
import { finalize } from 'rxjs';
import {
  ACADEMIC_DEGREE_VALUES,
  AcademicDegree,
  LABOR_SECTOR_VALUES,
  LaborSector,
  PROFESSION_VALUES,
  ProfessionalLine,
} from '../../../../core/models/api/user.model';
import { PrimaryButton } from '../../../../shared/components/buttons/primary-button/primary-button';
import { AutocompleteFormControl } from '../../../../shared/components/form-controls/autocomplete-form-control/autocomplete-form-control';
import { DropdownFormControl } from '../../../../shared/components/form-controls/dropdown-form-control/dropdown-form-control';
import { InputFormControl } from '../../../../shared/components/form-controls/input-form-control/input-form-control';
import { Tag } from '../../../../shared/components/tag/tag';
import { ArrowLeftOutline } from '../../../../shared/components/icons/arrow-left-outline';
import { ArrowRightOutline } from '../../../../shared/components/icons/arrow-right-outline';
import { ClockOutline } from '../../../../shared/components/icons/clock-outline';
import { CloseCircleOutline } from '../../../../shared/components/icons/close-circle-outline';
import { SendTwo } from '../../../../shared/components/icons/send-two';
import { ShieldLockBold } from '../../../../shared/components/icons/shield-lock-bold';
import { Image } from '../../../../shared/components/image/image';
import { ModalBase } from '../../../../shared/directives/modal-base';
import { DropdownOption } from '../../../../shared/models/form-controls.model';
import { UserState } from '../../services/user.state';

export interface ProfileProfessionalAnswers {
  usuario_profesion: string;
  usuario_profesion_otro?: string;
  usuario_grado_academico: AcademicDegree;
  usuario_grado_academico_otro?: string;
  usuario_linea_profesional: ProfessionalLine;
  usuario_linea_profesional_otro?: string;
  usuario_sector_laboral: LaborSector;
  usuario_sector_laboral_otro?: string;
}

type WizardStep = 0 | 1 | 2 | 3 | 4;
const PROFILE_PROFESSIONAL_SNOOZE_MAX = 3;

type ProfileProfessionalStepKey =
  | 'welcome'
  | 'profession'
  | 'academicDegree'
  | 'professionalLine'
  | 'laborSector';

interface ProfileProfessionalStepContent {
  key: ProfileProfessionalStepKey;
  titleParts: ProfileProfessionalTitlePart[];
  description: string;
}

interface ProfileProfessionalTitlePart {
  text: string;
  gradient?: boolean;
}

const PROFILE_PROFESSIONAL_STEPS: Record<
  WizardStep,
  ProfileProfessionalStepContent
> = {
  0: {
    key: 'welcome',
    titleParts: [
      { text: 'Personaliza', gradient: true },
      { text: ' tu ruta de aprendizaje' },
    ],
    description:
      'Cuéntanos sobre tu perfil para adaptar nuestras herramientas, cursos y recursos a tu nivel profesional.',
  },
  1: {
    key: 'profession',
    titleParts: [
      { text: '¿Cuál es tu ' },
      { text: 'profesión o carrera', gradient: true },
      { text: '?' },
    ],
    description:
      'Selecciona la profesión con la que te identificas actualmente.',
  },
  2: {
    key: 'academicDegree',
    titleParts: [
      { text: '¿Cuál es tu ' },
      { text: 'grado académico', gradient: true },
      { text: '?' },
    ],
    description: 'Cuéntanos para recomendarte un mejor contenido.',
  },
  3: {
    key: 'professionalLine',
    titleParts: [
      { text: '¿Qué ' },
      { text: 'área profesional', gradient: true },
      { text: ' quieres potenciar?' },
    ],
    description: 'Cuéntanos para recomendarte un mejor contenido.',
  },
  4: {
    key: 'laborSector',
    titleParts: [
      { text: '¿En qué ' },
      { text: 'sector', gradient: true },
      { text: ' trabajas actualmente?' },
    ],
    description: 'Cuéntanos para recomendarte un mejor contenido.',
  },
};

@Component({
  selector: 'profile-professional-modal',
  imports: [
    PrimaryButton,
    AutocompleteFormControl,
    DropdownFormControl,
    InputFormControl,
    Tag,
    Image,
    CloseCircleOutline,
    ShieldLockBold,
  ],
  templateUrl: './profile-professional-modal.html',
  styleUrl: './profile-professional-modal.scss',
})
export class ProfileProfessionalModal extends ModalBase<ProfileProfessionalAnswers> {
  readonly buttonVariant = uiConfig.primaryButtonVariant;

  private userState = inject(UserState);

  ClockOutline = ClockOutline;
  ArrowLeftOutline = ArrowLeftOutline;
  ArrowRightOutline = ArrowRightOutline;
  SendTwo = SendTwo;

  step = model<WizardStep>(0);
  steps = [1, 2, 3, 4] as const;
  loading = signal(false);
  navDir = signal<'forward' | 'back'>('forward');
  profession = signal<string | null>(null);
  professionOther = signal('');
  academicDegree = signal<AcademicDegree | null>(null);
  academicDegreeOther = signal('');
  professionalLine = signal<ProfessionalLine | null>(null);
  professionalLineOther = signal('');
  laborSector = signal<LaborSector | null>(null);
  laborSectorOther = signal('');

  professionOptions: DropdownOption<string>[] = PROFESSION_VALUES.map(
    value => ({ label: value, value })
  );
  academicDegreeOptions: DropdownOption<AcademicDegree>[] =
    ACADEMIC_DEGREE_VALUES.map(value => ({ label: value, value }));
  professionalLineOptions: DropdownOption<ProfessionalLine>[] =
    uiConfig.profileProfessionalPrompt.professionalLineOptions.map(value => ({
      label: value,
      value,
    }));
  laborSectorOptions: DropdownOption<LaborSector>[] = LABOR_SECTOR_VALUES.map(
    value => ({ label: value, value })
  );

  currentStep = computed(() => PROFILE_PROFESSIONAL_STEPS[this.step()]);
  canDismiss = computed(
    () =>
      (this.userState.user()?.usuario_perfil_profesional_veces_pospuesto ?? 0) <
      PROFILE_PROFESSIONAL_SNOOZE_MAX
  );
  imageSrc = computed(() => {
    return uiConfig.profileProfessionalPrompt.images[this.currentStep().key];
  });

  canContinue = computed(() => {
    switch (this.step()) {
      case 0:
        return true;
      case 1:
        return !!this.profession() && (this.profession() !== 'Otro' || !!this.professionOther().trim());
      case 2:
        return !!this.academicDegree() && (this.academicDegree() !== 'Otro' || !!this.academicDegreeOther().trim());
      case 3:
        return !!this.professionalLine() && (this.professionalLine() !== 'Otro' || !!this.professionalLineOther().trim());
      case 4:
        return !!this.laborSector() && (this.laborSector() !== 'Otro' || !!this.laborSectorOther().trim());
      default:
        return false;
    }
  });

  onDismiss() {
    if (this.loading() || !this.canDismiss()) return;
    this.exit();
  }

  onBack() {
    const current = this.step();
    if (current <= 0 || this.loading()) return;
    this.navDir.set('back');
    this.step.set((current - 1) as WizardStep);
  }

  onNext() {
    if (!this.canContinue() || this.loading()) return;
    const current = this.step();
    if (current === 4) {
      this.submitAnswers();
      return;
    }
    this.navDir.set('forward');
    this.step.set((current + 1) as WizardStep);
  }

  private submitAnswers() {
    const profesion = this.profession();
    const grado = this.academicDegree();
    const linea = this.professionalLine();
    const sector = this.laborSector();
    if (!profesion || !grado || !linea || !sector) return;

    const payload: ProfileProfessionalAnswers = {
      usuario_profesion: profesion,
      ...(profesion === 'Otro' && { usuario_profesion_otro: this.professionOther().trim() }),
      usuario_grado_academico: grado,
      ...(grado === 'Otro' && { usuario_grado_academico_otro: this.academicDegreeOther().trim() }),
      usuario_linea_profesional: linea,
      ...(linea === 'Otro' && { usuario_linea_profesional_otro: this.professionalLineOther().trim() }),
      usuario_sector_laboral: sector,
      ...(sector === 'Otro' && { usuario_sector_laboral_otro: this.laborSectorOther().trim() }),
    };

    this.loading.set(true);
    this.userState
      .update(payload)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe(() => this.send(payload));
  }
}
